import { CartGroupedSections, CartUpdateEvent } from "@theme/events";

/**
 * Shopify standard storefront events & actions.
 *
 * - `storefrontEvents`: helpers that dispatch the `shopify:*` promise-carrying events for the theme's own
 *   requests (cart mutations, variant selection, filters). Open the event with `start()` before the request,
 *   settle it with `resolveCart()` / `failCart()`.
 * - `storefrontActions`: configures `Shopify.actions.updateCart` / `openCart`, so apps update the cart UI
 *   without a page reload.
 *
 * The library (`window.StandardEvents`) is loaded by an async module in snippets/storefront-events.liquid and
 * is read lazily on every call: when it is missing (slow or blocked CDN) every helper is a no-op.
 * Both objects are also exposed on `window.FoxTheme`, because the configure stub rendered before
 * `content_for_header` has to call `configure()` synchronously on DOMContentLoaded.
 */

/** `sourceId` of the cart update dispatched after an app's `Shopify.actions.updateCart()`. */
export const ACTION_SOURCE = "storefront-action";

const getLibrary = () => window.StandardEvents || null;

/**
 * @param {AbortSignal} [signal]
 * @returns {Promise<any>} The AJAX cart.
 */
const fetchCart = async (signal) => {
  const response = await fetch(FoxTheme.routes.cart, { signal });
  if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
  return response.json();
};

export const storefrontEvents = {
  /** The standard events library, or null until (or unless) it loads. */
  get library() {
    return getLibrary();
  },

  /**
   * Dispatches a promise-carrying event (cart, product select, collection/search update) on `target` as the
   * operation starts. Returns the deferred to settle once it finishes, or null when the library isn't loaded.
   *
   * @param {EventTarget | null} target
   * @param {string} eventName - Class name in the library, e.g. `CartLinesUpdateEvent`.
   * @param {object} payload
   */
  start(target, eventName, payload) {
    const EventClass = getLibrary()?.[eventName];
    if (!EventClass || !target) return null;

    try {
      const deferred = EventClass.createPromise();
      const event = new EventClass({ ...payload, promise: deferred.promise });
      // Listeners attach their own handlers; these only keep an unobserved rejection out of the console.
      // The event carries a promise derived from the deferred's, so both need one.
      deferred.promise.catch(() => {});
      event.promise?.catch?.(() => {});
      target.dispatchEvent(event);
      return deferred;
    } catch (error) {
      console.error("[Pebble] Standard event dispatch failed", error);
      return null;
    }
  },

  /**
   * Returns the message of an AJAX cart error response, or null when the response is a cart.
   * @param {any} state
   * @returns {string | null}
   */
  getAjaxError(state) {
    if (!state) return null;
    if (state.status) return state.description || state.message || String(state.status);
    if (state.errors) {
      return typeof state.errors === "string" ? state.errors : Object.values(state.errors).flat().join(" ");
    }
    return null;
  },

  /**
   * Resolves a cart event. A declined change still resolves, with `userErrors` describing why.
   * The AJAX API answers a partly applied change (a quantity capped at the stock available) with the same
   * error as a declined one, so `isApplied(ajaxCart)` tells the two apart: when it returns true the message
   * is reported as a warning instead. When `cart` isn't a full AJAX cart, the current cart is fetched first.
   *
   * @param {any} deferred
   * @param {any} cart
   * @param {string | null} [errorMessage]
   * @param {((cart: any) => boolean) | null} [isApplied]
   */
  async resolveCart(deferred, cart, errorMessage = null, isApplied = null) {
    if (!deferred) return;
    // Settled from here on: an error thrown by the code that runs after this is not a failed cart request.
    deferred.settled = true;

    try {
      const ajaxCart = cart?.currency ? cart : await fetchCart();
      const result = { cart: getLibrary().CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart) };
      if (errorMessage && isApplied?.(ajaxCart)) {
        result.warnings = [{ code: "MERCHANDISE_NOT_ENOUGH_STOCK", message: errorMessage }];
      } else if (errorMessage) {
        result.userErrors = [{ code: "INVALID", message: errorMessage }];
      }
      deferred.resolve(result);
    } catch (error) {
      deferred.reject(error);
    }
  },

  /**
   * Rejects the cart events of a failed request, and tells every other listener through one
   * `shopify:cart:error`. `deferreds` is one deferred or the array one request opened. Events already settled
   * are left alone, so an error thrown after a request succeeded is not reported as a cart error.
   *
   * @param {EventTarget | null} target
   * @param {any} deferreds
   * @param {any} error
   */
  failCart(target, deferreds, error) {
    const pending = [].concat(deferreds).filter((deferred) => deferred && !deferred.settled);
    if (!pending.length) return;

    pending.forEach((deferred) => {
      deferred.settled = true;
      deferred.reject(error);
    });
    if (error?.name === "AbortError") return;

    const CartErrorEvent = getLibrary()?.CartErrorEvent;
    if (!CartErrorEvent || !target) return;

    target.dispatchEvent(
      new CartErrorEvent({
        error: error?.message || "Cart request failed",
        code: "SERVICE_UNAVAILABLE",
      })
    );
  },

  /**
   * Sends one cart request and reports it through the standard events opened for it (one request may need
   * several, e.g. one per lines-update action). Every event settles from the same response; a failed request
   * rejects them all and dispatches a single `shopify:cart:error`. Rethrows, so callers keep their own error
   * handling.
   *
   * @param {EventTarget} target
   * @param {Array<[string, object, ((cart: any) => boolean)?]>} events - `[eventName, payload, isApplied?]`.
   * @param {() => Promise<any>} request - Resolves with the parsed JSON response.
   */
  async trackCartRequest(target, events, request) {
    const deferreds = events.map(([eventName, payload, isApplied]) => {
      const deferred = this.start(target, eventName, payload);
      if (deferred) deferred.isApplied = isApplied;
      return deferred;
    });

    try {
      const response = await request();
      const errorMessage = this.getAjaxError(response);
      deferreds.forEach((deferred) =>
        this.resolveCart(deferred, errorMessage ? null : response, errorMessage, deferred?.isApplied)
      );
      return response;
    } catch (error) {
      this.failCart(target, deferreds, error);
      throw error;
    }
  },

  /**
   * Lines-update payload of a `change.js` request on the line `key`.
   * @param {string} key
   * @param {number} quantity
   */
  changeLinesPayload(key, quantity) {
    return { action: quantity === 0 ? "remove" : "update", lines: [{ id: key, quantity }] };
  },

  /**
   * Lines-update payload of an `add.js` request.
   * @param {Array<{ id: string | number, quantity?: number }>} items
   */
  addLinesPayload(items) {
    return {
      action: "add",
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: Number(item.quantity) || 1 })),
    };
  },

  /**
   * Converts a variant into the standard `ProductVariant` shape. Accepts both Liquid's `| json` shape
   * (price in cents, `options` array) and the `/products/<handle>.json` shape (decimal price string,
   * `option1`–`option3`). `optionNames` are the product's option names in position order.
   *
   * @param {any} variant
   * @param {string[]} [optionNames]
   */
  toVariant(variant, optionNames = []) {
    if (!variant) return null;

    const amount = typeof variant.price === "string" ? Number(variant.price) : variant.price / 100;
    const values = variant.options || [variant.option1, variant.option2, variant.option3].filter((v) => v != null);

    return {
      id: variant.id,
      title: variant.title,
      availableForSale: variant.available,
      price: {
        amount: amount.toFixed(2),
        currencyCode: window.Shopify?.currency?.active,
      },
      selectedOptions: values.map((value, index) => ({ name: optionNames[index], value })),
    };
  },
};

export const storefrontActions = {
  ACTION_SOURCE,

  /** @type {boolean} */
  configured: false,

  /** @type {AbortController | null} */
  refreshController: null,

  /**
   * Registers the theme's handlers. Only the first `configure()` call on an action takes effect, so this
   * runs from the stub rendered before `content_for_header`.
   */
  configure() {
    const actions = window.Shopify?.actions;
    if (!actions || this.configured) return;
    this.configured = true;

    actions.updateCart.configure({
      eventTarget: (meta) => this.getEventTarget(meta),
      handler: async (defaultHandler) => {
        const result = await defaultHandler();

        try {
          await this.refreshCart();
        } catch (error) {
          // A newer refresh superseded this one and renders the latest cart.
          if (error.name !== "AbortError") {
            console.error("[Pebble] Cart refresh after updateCart failed; reloading.", error);
            window.location.reload();
          }
        }

        return { ...result, detail: { ...result?.detail, handledBy: "pebble" } };
      },
    });

    actions.openCart.configure({
      handler: () => this.openCart(),
    });
  },

  /**
   * @param {{ type: string }} meta
   * @returns {Element | null} Null falls back to `document`.
   */
  getEventTarget(meta) {
    if (meta.type === "shopify:cart:note-update") return document.querySelector("cart-note");
    if (meta.type === "shopify:cart:discount-update") return document.querySelector("cart-discount");
    return document.querySelector("cart-drawer-component") || document.querySelector("cart-items-component");
  },

  /**
   * Re-renders every cart section on the page from the server, then dispatches `CartUpdateEvent` so the
   * existing subscribers (cart items, count bubble, quick order list, free shipping goal) update.
   * A newer call aborts an older one still in flight.
   *
   * @param {{ source?: string }} [options]
   */
  async refreshCart({ source = ACTION_SOURCE } = {}) {
    this.refreshController?.abort();
    const controller = new AbortController();
    this.refreshController = controller;
    const { signal } = controller;

    /** @type {string[]} */
    const sections = [];
    document.dispatchEvent(new CartGroupedSections(sections));
    const sectionIds = [...new Set(sections)];

    // Sections render in the context of the URL they're requested from, and the cart drawer section renders
    // nothing on the cart template: request them from the current page. `section-renderer` is not used: it
    // renders one section at a time and may hand back a cached or in-flight response.
    const [cart, renderedSections] = await Promise.all([
      fetchCart(signal),
      sectionIds.length
        ? fetch(`${window.location.pathname}?sections=${sectionIds.join(",")}`, { signal }).then((response) => {
            if (!response.ok) throw new Error(`Sections request failed: ${response.status}`);
            return response.json();
          })
        : null,
    ]);

    document.dispatchEvent(
      new CartUpdateEvent(cart, source, {
        source,
        itemCount: cart.item_count,
        sections: renderedSections ?? undefined,
      })
    );
    return cart;
  },

  async openCart() {
    const cartDrawer = /** @type {any} */ (document.querySelector("cart-drawer-component"));

    if (cartDrawer) {
      const quickAddDialog = /** @type {any} */ (document.querySelector("quick-add-dialog"));
      if (quickAddDialog?.refs?.dialog?.open) await quickAddDialog.closeDialog();
      cartDrawer.showDialog();
      return;
    }

    if (FoxTheme.template.name.split(".")[0] === "cart") {
      document.querySelector("cart-items-component")?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    window.location.href = FoxTheme.routes.cart_url;
  },
};

window.FoxTheme = window.FoxTheme || {};
window.FoxTheme.storefrontEvents = storefrontEvents;
window.FoxTheme.storefrontActions = storefrontActions;
